import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import useSearchStore from '../../store/searchStore';

// WHOIS bilgilerini render eden yardımcı fonksiyon
const WhoisSection = ({ whoisData }) => {
  if (!whoisData || whoisData.length === 0) return null;

  // Önemli WHOIS alanları
  const importantFields = [
    'inetnum',
    'netname',
    'status',
    'owner',
    'ownerid',
    'responsible',
    'address',
    'country',
    'phone',
    'e-mail',
    'abuse-c',
    'created',
    'last-modified'
  ];

  return (
    <div>
      <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">WHOIS Information</h3>
      <div className="space-y-4">
        {whoisData.map((section, index) => {
          // Önemli alanları filtrele
          const filteredItems = section.filter(item => importantFields.includes(item.key));
          if (filteredItems.length === 0) return null;

          return (
            <div key={index} className="bg-gray-50 rounded-lg p-3">
              {filteredItems.map((item, i) => (
                <div key={i} className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-500 font-medium">{item.key}:</span>
                  {item.details_link ? (
                    <a 
                      href={item.details_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <span className="text-gray-900">{item.value}</span>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Historical WHOIS bilgilerini render eden yardımcı fonksiyon
const HistoricalWhoisSection = ({ historicalData }) => {
  if (!historicalData || historicalData.length === 0) return null;

  // Önemli Historical WHOIS attribute'ları
  const importantAttributes = [
    'inetnum',
    'netname',
    'descr',
    'country',
    'status',
    'source',
    'created',
    'last-modified'
  ];

  return (
    <div>
      <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Historical WHOIS</h3>
      <div className="space-y-4">
        {historicalData.map((record, index) => {
          // Önemli attribute'ları filtrele
          const filteredAttributes = record.attributes.filter(attr => 
            importantAttributes.includes(attr.attribute) && attr.value.trim() !== ''
          );

          if (filteredAttributes.length === 0) return null;

          return (
            <div key={index} className="bg-gray-50 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                <span className="text-gray-500 font-medium">From Time:</span>
                <span className="text-gray-900">{new Date(record.from_time).toLocaleString()}</span>
              </div>
              <div className="mt-2">
                <div className="space-y-1">
                  {filteredAttributes.map((attr, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-500">{attr.attribute}:</span>
                      <span className="text-gray-900">{attr.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function SearchPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const { searchResults: result, isLoading: loading, error, search, clearSearch } = useSearchStore();

  useEffect(() => {
    const queryParam = searchParams.get('q');
    if (queryParam) {
      setQuery(queryParam);
      search({ query: queryParam.trim() });
    }
  }, [searchParams, search]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    try {
      setSearchParams({ q: query.trim() });
      await search({ query: query.trim() });
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  // Export fonksiyonu
  const handleExport = () => {
    if (!result) return;

    // Export edilecek veriyi hazırla
    const exportData = {
      query_info: {
        host: result.host,
        type: result.type,
        confidence: result.confidence,
        first_seen: result.firstSeen,
        last_seen: result.lastSeen,
        last_update: result.lastUpdate
      },
      location: {
        country: result.country?.country,
        country_code: result.country?.countryCode,
        city: result.country?.city,
        coordinates: {
          latitude: result.latitude,
          longitude: result.longitude
        }
      },
      network_info: {
        organization: result.organization,
        isp: result.isp,
        asn: result.asn,
        hostnames: result.hostnames,
        domains: result.domains
      },
      security_info: {
        open_ports: result.data?.ports || []
      },
      whois_info: result.data?.whois || [],
      historical_whois: result.data?.historical_whois || []
    };

    // JSON dosyasını oluştur ve indir
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `threat-intel-${result.host}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // HTTP portlarını kontrol eden fonksiyon
  const isHttpPort = (port) => {
    const httpPorts = [80, 443, 8080, 8443, 3000, 4000, 4433, 8000, 8888];
    return httpPorts.includes(Number(port));
  };

  // Port için URL oluşturan fonksiyon
  const getPortUrl = (port, host) => {
    const protocol = port === 443 || port === 8443 ? 'https' : 'http';
    return `${protocol}://${host}:${port}`;
  };

  // Güvenli tarih formatlaması
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid date';
    }
  };

  // Ülke kodundan bayrak emojisi oluşturan fonksiyon
  const getCountryFlag = (countryCode) => {
    if (!countryCode) return null;
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Search Intelligence</h1>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            Filters
          </button>
          {result && (
            <button
              onClick={handleExport}
              className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-300 rounded-lg hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export
            </button>
          )}
          <button 
            onClick={() => {
              setQuery('');
              clearSearch();
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            New Search
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <form onSubmit={handleSearch} className="p-4 border-b border-gray-200">
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for IP addresses, domains, or threats..."
              className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 placeholder-gray-500 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </form>

        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900">Searching...</h3>
              <p className="mt-2 text-sm text-gray-500 text-center max-w-md">
                This might take a few moments. We're gathering intelligence from multiple sources.
              </p>
            </div>
          ) : error ? (
            <div className="text-center text-red-500">
              <p>{error}</p>
            </div>
          ) : result ? (
            <div className="space-y-6">
              {/* Header Bilgileri */}
              <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
                <span className={`px-2 py-1 text-xs font-medium text-white rounded ${
                  result.confidence >= 8 ? 'bg-red-500' : 'bg-blue-500'
                }`}>
                  {result.confidence || 'N/A'}/10
                </span>
                {result.country && (
                  <span className="flex items-center gap-2 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                    <span>{getCountryFlag(result.country.countryCode)}</span>
                    {result.country.country} ({result.country.countryCode})
                  </span>
                )}
                <code className="text-sm text-gray-900 font-mono">{result.host || 'N/A'}</code>
              </div>

              {/* Detay Bilgileri */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sol Kolon */}
                <div className="space-y-4">
                  {result.hostnames && result.hostnames.length > 0 && (
                    <div>
                      <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Hostnames</h3>
                      <div className="space-y-1">
                        {result.hostnames.map((hostname, index) => (
                          <div key={index} className="px-2 py-1 text-sm text-gray-600 bg-gray-100 rounded">
                            {hostname}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.domains && result.domains.length > 0 && (
                    <div>
                      <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Domains</h3>
                      <div className="space-y-1">
                        {result.domains.map((domain, index) => (
                          <div key={index} className="px-2 py-1 text-sm text-gray-600 bg-gray-100 rounded">
                            {domain}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Details</h3>
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">Type:</span>
                        <span className="text-sm text-gray-900">{result.type || 'N/A'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">Organization:</span>
                        <span className="text-sm text-gray-900">{result.organization || 'N/A'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">ISP:</span>
                        <span className="text-sm text-gray-900">{result.isp || 'N/A'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">ASN:</span>
                        <span className="text-sm text-gray-900">{result.asn || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* WHOIS Bilgileri */}
                  {result.data?.whois && (
                    <WhoisSection whoisData={result.data.whois} />
                  )}
                </div>

                {/* Sağ Kolon */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Location</h3>
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">City:</span>
                        <span className="text-sm text-gray-900">{result.country?.city || 'N/A'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">Coordinates:</span>
                        <span className="text-sm text-gray-900">
                          {result.latitude && result.longitude ? `${result.latitude}, ${result.longitude}` : 'N/A'}
                        </span>
                      </div>
                      {result?.latitude && result?.longitude && (
                        <div className="mt-4">
                          <div className="h-[300px] w-full rounded-lg overflow-hidden border border-gray-200">
                            <iframe
                              title="Location Map"
                              width="100%"
                              height="100%"
                              frameBorder="0"
                              scrolling="no"
                              marginHeight="0"
                              marginWidth="0"
                              src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(result.longitude)-0.01},${parseFloat(result.latitude)-0.01},${parseFloat(result.longitude)+0.01},${parseFloat(result.latitude)+0.01}&layer=mapnik&marker=${result.latitude},${result.longitude}`}
                            />
                          </div>
                          <div className="mt-2">
                            <a
                              href={`https://www.openstreetmap.org/?mlat=${result.latitude}&mlon=${result.longitude}#map=15/${result.latitude}/${result.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              View Larger Map →
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Timestamps</h3>
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">First Seen:</span>
                        <span className="text-sm text-gray-900">
                          {formatDate(result.firstSeen)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">Last Seen:</span>
                        <span className="text-sm text-gray-900">
                          {formatDate(result.lastSeen)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">Last Update:</span>
                        <span className="text-sm text-gray-900">
                          {formatDate(result.lastUpdate)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {result.data?.ports && result.data.ports.length > 0 && (
                    <div>
                      <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Open Ports</h3>
                      <div className="flex flex-wrap gap-2">
                        {result.data.ports.map((port, index) => (
                          <span key={index} className={`px-2 py-1 text-xs font-medium ${
                            isHttpPort(port) 
                              ? 'text-blue-600 hover:text-blue-800 cursor-pointer' 
                              : 'text-gray-600'
                          } bg-gray-100 rounded`}>
                            {isHttpPort(port) ? (
                              <a
                                href={getPortUrl(port, result.host)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {port}
                              </a>
                            ) : (
                              port
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Historical WHOIS Bilgileri */}
                  {result.data?.historical_whois && (
                    <HistoricalWhoisSection historicalData={result.data.historical_whois} />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No search results</h3>
              <p className="mt-1 text-sm text-gray-500">
                Start by searching for an IP address, domain, or threat.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchPanel; 